import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaTimes, FaBolt, FaToggleOn, FaToggleOff,
    FaSync, FaPlay, FaCheckCircle, FaExclamationTriangle,
    FaTruck, FaClipboardList, FaUserCheck, FaCog,
    FaCamera, FaClipboardCheck, FaPlus, FaTag,
    FaTools,
} from 'react-icons/fa';
import './ModalAutoDistribuicao.css';

/* ── Formatador de data ───────────────────────────────────────── */
const fmtDateTime = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};

/* ── Stepper ──────────────────────────────────────────────────── */
const Stepper = ({ value, onChange, min = 1, max = 20, step = 1, disabled }) => (
    <div className="mad-stepper">
        <button type="button" className="mad-step-btn"
            onClick={() => onChange(Math.max(min, value - step))}
            disabled={disabled || value <= min}>−</button>
        <span className="mad-step-val">{value}</span>
        <button type="button" className="mad-step-btn"
            onClick={() => onChange(Math.min(max, value + step))}
            disabled={disabled || value >= max}>+</button>
    </div>
);

/* ── Stat card ────────────────────────────────────────────────── */
const StatBox = ({ icon: Icon, label, value, color }) => (
    <div className="mad-stat-box" style={{ '--sc': color }}>
        <div className="mad-stat-icon"><Icon /></div>
        <div className="mad-stat-body">
            <span className="mad-stat-value">{value ?? '—'}</span>
            <span className="mad-stat-label">{label}</span>
        </div>
    </div>
);

/* ── Labels de origem ────────────────────────────────────────── */
const ORIGENS = [
    { value: 'interno',    label: 'Interno',    emoji: '🟠' },
    { value: 'seguradora', label: 'Seguradora', emoji: '🔵' },
    { value: 'app',        label: 'App Mobile', emoji: '🟢' },
];

// ── campos que podem ter obrigatoriedade dispensada nas OS de Tarefa ──────────
const TAREFA_CAMPOS = [
    { key: 'valor',     label: 'Valor da despesa', emoji: '💰', desc: 'Custo total da tarefa (R$)'         },
    { key: 'placa',     label: 'Placa do veículo', emoji: '🚗', desc: 'Identificação do veículo utilizado' },
    { key: 'hodometro', label: 'Hodômetro (km)',   emoji: '📏', desc: 'Quilometragem atual do veículo'     },
];

const defaultExcecoes = () => ({
    fotos:     { origens: [], os_ids: [] },
    checklist: { origens: [], os_ids: [] },
    tarefa:    { campos_opcionais: [] },         // ← NOVO
});

/* ── Tag Input ─────────────────────────────────────────────────── */
const OsIdTagInput = ({ ids, onChange, disabled }) => {
    const [input, setInput] = useState('');
    const inputRef = useRef(null);

    const add = () => {
        const val = input.trim().toUpperCase();
        if (!val) return;
        if (ids.includes(val)) { setInput(''); return; }
        onChange([...ids, val]);
        setInput('');
        inputRef.current?.focus();
    };

    const remove = (id) => onChange(ids.filter(i => i !== id));

    return (
        <div className="mad-tag-wrap">
            <div className="mad-tags-list">
                {ids.map(id => (
                    <span key={id} className="mad-tag">
                        <FaTag size={9} /> {id}
                        {!disabled && (
                            <button onClick={() => remove(id)} className="mad-tag-remove" title="Remover">✕</button>
                        )}
                    </span>
                ))}
                {ids.length === 0 && (
                    <span className="mad-tag-placeholder">Nenhuma OS específica</span>
                )}
            </div>
            {!disabled && (
                <div className="mad-tag-input-row">
                    <input
                        ref={inputRef}
                        type="text"
                        className="mad-tag-input"
                        placeholder="Ex: 0525-0001"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                        disabled={disabled}
                    />
                    <button
                        type="button"
                        className="mad-tag-add-btn"
                        onClick={add}
                        disabled={!input.trim() || disabled}
                        title="Adicionar OS"
                    >
                        <FaPlus size={9} />
                    </button>
                </div>
            )}
        </div>
    );
};

/* ── Card de exceção (fotos / checklist) ──────────────────────── */
const ExcecaoCard = ({ icon: Icon, title, desc, excecoes, onChange, disabled }) => {
    const toggleOrigem = (origem) => {
        const origens = excecoes.origens.includes(origem)
            ? excecoes.origens.filter(o => o !== origem)
            : [...excecoes.origens, origem];
        onChange({ ...excecoes, origens });
    };

    const allActive = ORIGENS.every(o => excecoes.origens.includes(o.value));
    const toggleAll = () => onChange({ ...excecoes, origens: allActive ? [] : ORIGENS.map(o => o.value) });
    const hasAnyException = excecoes.origens.length > 0 || excecoes.os_ids.length > 0;

    return (
        <div className={`mad-excecao-card ${hasAnyException ? 'has-exception' : ''}`}>
            <div className="mad-excecao-header">
                <div className="mad-excecao-title-row">
                    <div className="mad-excecao-icon"><Icon /></div>
                    <div>
                        <div className="mad-excecao-title">{title}</div>
                        <div className="mad-excecao-desc">{desc}</div>
                    </div>
                </div>
                {hasAnyException && (
                    <span className="mad-excecao-badge">
                        {excecoes.origens.length + excecoes.os_ids.length} exceção(ões)
                    </span>
                )}
            </div>

            <div className="mad-excecao-body">
                <div className="mad-excecao-section-label">
                    Desativar por origem
                    <button type="button" className="mad-excecao-all-btn" onClick={toggleAll} disabled={disabled}>
                        {allActive ? 'Desmarcar todas' : 'Marcar todas'}
                    </button>
                </div>
                <div className="mad-excecao-chips">
                    {ORIGENS.map(o => {
                        const active = excecoes.origens.includes(o.value);
                        return (
                            <button
                                key={o.value} type="button"
                                className={`mad-excecao-chip ${active ? 'active' : ''}`}
                                onClick={() => !disabled && toggleOrigem(o.value)}
                                disabled={disabled}
                            >
                                <span>{o.emoji}</span>
                                <span>{o.label}</span>
                                {active && <FaCheckCircle size={9} className="mad-chip-check" />}
                            </button>
                        );
                    })}
                </div>

                <div className="mad-excecao-section-label" style={{ marginTop: 10 }}>
                    Desativar por número de OS
                </div>
                <OsIdTagInput
                    ids={excecoes.os_ids}
                    onChange={ids => onChange({ ...excecoes, os_ids: ids })}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

/* ── Card de campos opcionais da Tarefa ─────────────────────────
   Permite ao admin dispensar a obrigatoriedade de valor, placa e
   hodômetro ao finalizar uma OS de tarefa.
   A mudança é refletida no app via Socket.io (sem rebuild).
   ─────────────────────────────────────────────────────────────── */
const TarefaCamposCard = ({ excecoes, onChange, disabled }) => {
    const campos = excecoes?.campos_opcionais || [];

    const toggle = (key) => {
        const next = campos.includes(key)
            ? campos.filter(k => k !== key)
            : [...campos, key];
        onChange({ campos_opcionais: next });
    };

    const hasExceptions = campos.length > 0;

    return (
        <div className={`mad-excecao-card mad-excecao-card--tarefa ${hasExceptions ? 'has-exception' : ''}`}>
            <div className="mad-excecao-header">
                <div className="mad-excecao-title-row">
                    <div className="mad-excecao-icon mad-excecao-icon--tarefa"><FaTools /></div>
                    <div>
                        <div className="mad-excecao-title">🔧 Campos opcionais — Tarefa</div>
                        <div className="mad-excecao-desc">
                            Campos marcados deixam de ser obrigatórios ao finalizar uma OS de tarefa
                        </div>
                    </div>
                </div>
                {hasExceptions && (
                    <span className="mad-excecao-badge mad-excecao-badge--tarefa">
                        {campos.length} campo(s)
                    </span>
                )}
            </div>

            <div className="mad-excecao-body">
                <div className="mad-excecao-section-label">Tornar opcional no app</div>
                <div className="mad-excecao-chips">
                    {TAREFA_CAMPOS.map(campo => {
                        const active = campos.includes(campo.key);
                        return (
                            <button
                                key={campo.key} type="button"
                                className={`mad-excecao-chip mad-excecao-chip--tarefa ${active ? 'active' : ''}`}
                                onClick={() => !disabled && toggle(campo.key)}
                                disabled={disabled}
                                title={campo.desc}
                            >
                                <span>{campo.emoji}</span>
                                <span>{campo.label}</span>
                                {active && <FaCheckCircle size={9} className="mad-chip-check" />}
                            </button>
                        );
                    })}
                </div>

                {hasExceptions ? (
                    <div className="mad-tarefa-campos-hint">
                        ✅ {campos.map(k => TAREFA_CAMPOS.find(c => c.key === k)?.label || k).join(', ')}{' '}
                        {campos.length === 1 ? 'será exibido como' : 'serão exibidos como'} <strong>opcional</strong> no app
                        durante a finalização de OS de tarefa. Efetivo após salvar.
                    </div>
                ) : (
                    <div className="mad-tarefa-campos-hint mad-tarefa-campos-hint--neutral">
                        ℹ️ Nenhum campo dispensado. Valor, Placa e Hodômetro são todos obrigatórios.
                    </div>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MODAL PRINCIPAL
═══════════════════════════════════════════════════════════════ */
export default function ModalAutoDistribuicao({ isOpen, onClose, user }) {

    const isAllowed = user?.perfil === 'admin' || user?.perfil === 'admin_geral';

    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);
    const [executing,  setExecuting]  = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [ativo,         setAtivo]        = useState(false);
    const [limite,        setLimite]        = useState(3);
    const [janela,        setJanela]        = useState(15);
    const [consAgendadas, setConsAgendadas] = useState(true);
    const [excecoes,      setExcecoes]      = useState(defaultExcecoes());

    const [stats,             setStats]             = useState(null);
    const [ultimaExecucao,    setUltimaExecucao]    = useState(null);
    const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
    const [resultadoManual,   setResultadoManual]   = useState(null);

    /* ── Carrega config ───────────────────────────────────────── */
    const loadConfig = useCallback(async () => {
        if (!isOpen || !isAllowed) return;
        try {
            const { data } = await api.get('/api/ordens/auto-distribuicao/config');
            setAtivo(!!data.config.ativo);
            setLimite(data.config.limite_por_motorista || 3);
            setJanela(data.config.janela_online_minutos || 15);
            setConsAgendadas(!!data.config.considerar_agendadas);
            setStats(data.stats || null);
            setUltimaExecucao(data.ultima_execucao || null);
            setUltimaAtualizacao(data.config.updated_at || null);

            const exc = data.config.excecoes_finalizacao;
            setExcecoes({
                fotos:     { origens: exc?.fotos?.origens     || [], os_ids: exc?.fotos?.os_ids     || [] },
                checklist: { origens: exc?.checklist?.origens || [], os_ids: exc?.checklist?.os_ids || [] },
                tarefa:    { campos_opcionais: exc?.tarefa?.campos_opcionais || [] },   // ← NOVO
            });
        } catch {
            toast.error('Erro ao carregar configurações.');
        } finally {
            setLoading(false);
        }
    }, [isOpen, isAllowed]);

    useEffect(() => {
        if (isOpen) { setLoading(true); loadConfig(); }
    }, [isOpen, loadConfig]);

    useEffect(() => {
        if (!isOpen) return;
        const id = setInterval(async () => {
            try {
                const { data } = await api.get('/api/ordens/auto-distribuicao/config');
                setStats(data.stats || null);
                setUltimaExecucao(data.ultima_execucao || null);
            } catch {}
        }, 20000);
        return () => clearInterval(id);
    }, [isOpen]);

    /* ── Salva ────────────────────────────────────────────────── */
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/api/ordens/auto-distribuicao/config', {
                ativo: ativo ? 1 : 0,
                limite_por_motorista:  limite,
                janela_online_minutos: janela,
                considerar_agendadas:  consAgendadas ? 1 : 0,
                excecoes_finalizacao:  excecoes,   // inclui fotos + checklist + tarefa
            });
            toast.success('Configuração salva! O app mobile foi notificado.');
            loadConfig();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao salvar.');
        } finally {
            setSaving(false);
        }
    };

    /* ── Executa rodada manual ────────────────────────────────── */
    const handleExecutar = async () => {
        setExecuting(true);
        setResultadoManual(null);
        try {
            const { data } = await api.post('/api/ordens/auto-distribuicao/executar');
            setResultadoManual(data);
            if (data.atribuicoes?.length > 0)
                toast.success(`✅ ${data.atribuicoes.length} OS distribuída(s)!`);
            else
                toast.info('Nenhuma OS distribuída no momento.');
            loadConfig();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao executar.');
        } finally {
            setExecuting(false);
        }
    };

    const handleRefreshStats = async () => {
        setRefreshing(true);
        await loadConfig();
        setRefreshing(false);
    };

    const totalExcecoes =
        excecoes.fotos.origens.length     + excecoes.fotos.os_ids.length +
        excecoes.checklist.origens.length + excecoes.checklist.os_ids.length +
        (excecoes.tarefa?.campos_opcionais?.length || 0);  // ← NOVO

    if (!isOpen || !isAllowed) return null;

    /* ─── RENDER ─────────────────────────────────────────────── */
    return (
        <div className="mad-backdrop" onClick={onClose}>
            <div className="mad-modal" onClick={e => e.stopPropagation()}>

                {/* ══ HEADER ══ */}
                <div className="mad-header">
                    <div className="mad-header-left">
                        <div className="mad-header-icon"><FaBolt /></div>
                        <div>
                            <h3>Distribuição Automática</h3>
                            <p>Atribuição inteligente de OS · Exceções de finalização</p>
                        </div>
                    </div>
                    <button className="mad-close" onClick={onClose} title="Fechar"><FaTimes /></button>
                </div>

                {loading ? (
                    <div className="mad-loading">
                        <FaSync className="mad-spin" />
                        <span>Carregando configurações...</span>
                    </div>
                ) : (
                    <>
                        {/* ══ BODY — 2 COLUNAS ══ */}
                        <div className="mad-body-grid">

                            {/* ─── COLUNA ESQUERDA — distribuição ─────────────────── */}
                            <div className="mad-col-left">

                                {/* Toggle */}
                                <div className={`mad-toggle-card ${ativo ? 'on' : 'off'}`}>
                                    <div className="mad-toggle-left">
                                        <div className="mad-toggle-title">
                                            {ativo ? '⚡ Distribuição automática ATIVA' : '⏸ Distribuição automática INATIVA'}
                                        </div>
                                        <div className="mad-toggle-sub">
                                            {ativo
                                                ? 'OS na fila serão atribuídas automaticamente a motoristas livres.'
                                                : 'As OS permanecem na fila até atribuição manual.'}
                                        </div>
                                        {ultimaAtualizacao && (
                                            <div className="mad-toggle-meta">
                                                Última alteração: {fmtDateTime(ultimaAtualizacao)}
                                            </div>
                                        )}
                                    </div>
                                    <button className={`mad-toggle-btn ${ativo ? 'on' : 'off'}`}
                                        onClick={() => setAtivo(v => !v)}>
                                        {ativo ? <FaToggleOn /> : <FaToggleOff />}
                                    </button>
                                </div>

                                {/* Stats 2×2 */}
                                <div className="mad-stats-row">
                                    <StatBox icon={FaClipboardList} label="OS na fila"       value={stats?.os_na_fila}   color="#f59e0b" />
                                    <StatBox icon={FaTruck}         label="Motoristas online" value={stats?.online}       color="#3b82f6" />
                                    <StatBox icon={FaUserCheck}     label="Livres agora"      value={stats?.livres}       color="#10b981" />
                                    <StatBox icon={FaCog}           label="Em andamento"      value={stats?.em_andamento} color="#8b5cf6" />
                                </div>
                                <div className="mad-stats-refresh">
                                    <button onClick={handleRefreshStats} disabled={refreshing} className="mad-btn-ghost-sm">
                                        <FaSync className={refreshing ? 'mad-spin' : ''} /> Atualizar
                                    </button>
                                </div>

                                {/* Configurações */}
                                <div className="mad-config-section">
                                    <h4 className="mad-section-title"><FaCog /> Configurações</h4>

                                    <div className="mad-config-row">
                                        <div className="mad-config-item">
                                            <label>Limite de OS por motorista</label>
                                            <p className="mad-config-hint">
                                                Ao atingir este número de OS ativas, o motorista é ignorado.
                                            </p>
                                            <Stepper value={limite} onChange={setLimite} min={1} max={20} />
                                        </div>
                                        <div className="mad-config-item">
                                            <label>Janela de atividade GPS</label>
                                            <p className="mad-config-hint">
                                                Motoristas sem GPS neste período são considerados offline.
                                            </p>
                                            <Stepper value={janela} onChange={setJanela} min={5} max={120} step={5} />
                                            <span className="mad-step-unit">minutos</span>
                                        </div>
                                    </div>

                                    <label className="mad-checkbox-row">
                                        <input type="checkbox" checked={consAgendadas}
                                            onChange={e => setConsAgendadas(e.target.checked)} />
                                        <span>Considerar OS <strong>Agendadas</strong> na contagem do limite</span>
                                    </label>
                                </div>
                            </div>

                            {/* ─── COLUNA DIREITA — exceções ───────────────────────── */}
                            <div className="mad-col-right">
                                <div className="mad-config-section">
                                    <h4 className="mad-section-title">
                                        <FaExclamationTriangle />
                                        Exceções de Finalização
                                        {totalExcecoes > 0 && (
                                            <span className="mad-excecoes-count-badge">{totalExcecoes}</span>
                                        )}
                                    </h4>

                                    <div className="mad-excecao-intro">
                                        <span className="mad-excecao-intro-icon">⚠️</span>
                                        <div>
                                            <strong>Apenas admin pode configurar.</strong>{' '}
                                            Quando marcado, o motorista <strong>não precisa</strong> enviar
                                            aquele item ao finalizar. A mudança reflete no app{' '}
                                            <strong>imediatamente</strong> via Socket.io.
                                        </div>
                                    </div>

                                    {/* ── OS de Serviço ── */}
                                    <ExcecaoCard
                                        icon={FaCamera}
                                        title="📸 Fotos obrigatórias"
                                        desc="4 fotos do veículo (Frente, Traseira, Lados)"
                                        excecoes={excecoes.fotos}
                                        onChange={novas => setExcecoes(prev => ({ ...prev, fotos: novas }))}
                                        disabled={saving}
                                    />

                                    <ExcecaoCard
                                        icon={FaClipboardCheck}
                                        title="📋 Checklist do veículo"
                                        desc="Vistoria rápida anti-glosa (lataria, vidros, pneus, pertences)"
                                        excecoes={excecoes.checklist}
                                        onChange={novas => setExcecoes(prev => ({ ...prev, checklist: novas }))}
                                        disabled={saving}
                                    />

                                    {/* ── OS de Tarefa — campos opcionais ── */}
                                    <TarefaCamposCard
                                        excecoes={excecoes.tarefa}
                                        onChange={novas => setExcecoes(prev => ({ ...prev, tarefa: novas }))}
                                        disabled={saving}
                                    />

                                    {totalExcecoes > 0 && (
                                        <button
                                            type="button"
                                            className="mad-excecao-reset-btn"
                                            onClick={() => setExcecoes(defaultExcecoes())}
                                            disabled={saving}
                                        >
                                            Remover todas as exceções
                                        </button>
                                    )}
                                </div>

                                {/* Resultado da última rodada manual */}
                                {resultadoManual && (
                                    <div className={`mad-result ${resultadoManual.atribuicoes?.length > 0 ? 'success' : 'empty'}`}>
                                        {resultadoManual.atribuicoes?.length > 0 ? (
                                            <>
                                                <div className="mad-result-title">
                                                    <FaCheckCircle /> {resultadoManual.atribuicoes.length} OS distribuída(s)
                                                </div>
                                                <ul className="mad-result-list">
                                                    {resultadoManual.atribuicoes.map((a, i) => (
                                                        <li key={i}>
                                                            <span className="mad-result-os">OS #{a.os_id}</span>
                                                            <span className="mad-result-arrow">→</span>
                                                            <span className="mad-result-driver">🚛 {a.motorista_nome}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            <div className="mad-result-title empty">
                                                <FaExclamationTriangle /> {resultadoManual.message}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Última execução automática */}
                                {ultimaExecucao && !resultadoManual && (
                                    <div className="mad-last-exec">
                                        <span className="mad-last-exec-label">
                                            Última execução ({ultimaExecucao.origem}) — {fmtDateTime(ultimaExecucao.executado_em)}
                                        </span>
                                        {ultimaExecucao.os_atribuidas > 0 ? (
                                            <span className="mad-last-exec-count success">
                                                {ultimaExecucao.os_atribuidas} OS atribuída(s)
                                            </span>
                                        ) : ultimaExecucao.erro ? (
                                            <span className="mad-last-exec-count error">Erro: {ultimaExecucao.erro.slice(0, 60)}</span>
                                        ) : (
                                            <span className="mad-last-exec-count neutral">Nenhuma OS distribuída</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* ══ FOOTER ══ */}
                <div className="mad-footer">
                    <button className="mad-btn-execute" onClick={handleExecutar}
                        disabled={executing || loading}>
                        {executing
                            ? <><FaSync className="mad-spin" /> Executando...</>
                            : <><FaPlay /> Executar agora</>}
                    </button>
                    <div className="mad-footer-right">
                        <button className="mad-btn-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
                        <button className="mad-btn-save" onClick={handleSave} disabled={saving || loading}>
                            {saving
                                ? <><FaSync className="mad-spin" /> Salvando...</>
                                : <><FaCheckCircle /> Salvar configuração</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}